import {
  type IAIReceptionistCustomQA,
  type IAIReceptionistCustomSection,
  type IAIReceptionistKnowledgeBase,
  type IAIReceptionistSectionQaPairs,
  type IImportProposal,
  type IImportProposalFields,
  type IImportProposedCustomSection,
  type IImportProposedQaPair,
  IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS,
  KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS,
  type SectionQaPairLists,
} from '@principle-theorem/principle-core/interfaces';
import { isNil, isString } from 'lodash';
import { IMPORT_PROPOSAL_FIELDS, ImportProposal } from './import-proposal';
import { ImportProposalMatching } from './import-proposal-matching';

/**
 * What the practice ticked in the import review. Proposed Q&A pairs and custom
 * sections have no ids until they are merged, so they are selected by their
 * index in the proposal.
 */
export type AcceptedImportProposalFields = Partial<
  Record<keyof IImportProposalFields, true>
>;

export interface IAcceptedImportProposal {
  fields: AcceptedImportProposalFields;
  /** Accepted indexes into each `IImportProposal.sectionQaPairs` list. */
  sectionQaPairs: SectionQaPairLists<number>;
  /** Accepted indexes into `IImportProposal.customSections`. */
  customSections: number[];
}

/**
 * Owns what the practice accepts from an Import Proposal: which proposed
 * values start accepted in the review, and how accepted values reach the
 * editor's unsaved Knowledge Base.
 */
export class ImportProposalAcceptance {
  /**
   * Decides whether a proposed value starts accepted in the review: only a
   * field the practice has not filled in does. `false` and `0` are answers the
   * practice gave, so a non-text field is empty only when it has no value.
   */
  static isFieldEmpty(
    knowledgeBase: IAIReceptionistKnowledgeBase | undefined,
    field: keyof IImportProposalFields
  ): boolean {
    const value = knowledgeBase?.[field];
    if (isString(value)) {
      return value.trim() === '';
    }
    return isNil(value);
  }

  /**
   * Applies what the practice accepted to the editor's unsaved Knowledge Base.
   * It returns a new Knowledge Base and never saves or pushes; the editor's
   * existing Save does that. Accepted fields replace the current value. An
   * accepted Q&A pair or custom section that references an existing item
   * still in the Knowledge Base updates it in place: only the pair's answer,
   * or the section's content, is replaced. Every other accepted pair and
   * section is appended as a new item with ids from `newId`, up to the
   * provider's collection cap. Provenance and references are for the review
   * only and are never copied across. `newId` is passed in so this stays free
   * of any runtime's id generator.
   */
  static merge(
    knowledgeBase: IAIReceptionistKnowledgeBase,
    proposal: IImportProposal,
    accepted: IAcceptedImportProposal,
    newId: () => string
  ): IAIReceptionistKnowledgeBase {
    const merged: IAIReceptionistKnowledgeBase = { ...knowledgeBase };
    IMPORT_PROPOSAL_FIELDS.forEach((field) =>
      ImportProposal.assignIfDefined(
        merged,
        field,
        accepted.fields[field] ? proposal.fields[field]?.value : undefined
      )
    );
    ImportProposal.assignIfDefined(
      merged,
      'sectionQaPairs',
      ImportProposalAcceptance._mergeSectionQaPairs(
        knowledgeBase.sectionQaPairs,
        proposal.sectionQaPairs,
        accepted.sectionQaPairs,
        newId
      )
    );
    ImportProposal.assignIfDefined(
      merged,
      'customSections',
      ImportProposalAcceptance._mergeCustomSections(
        knowledgeBase.customSections,
        proposal.customSections,
        accepted.customSections,
        newId
      )
    );
    return merged;
  }

  /**
   * Splits accepted items into those that update the existing item they
   * reference and those to append as new: an item whose target is gone, or
   * already updated by an earlier item, is appended instead.
   */
  private static _splitUpdates<Item, Ref>(
    accepted: Item[],
    refOf: (item: Item) => Ref | undefined,
    isTarget: (ref: Ref) => boolean
  ): { updates: Item[]; additions: Item[] } {
    const keepsRef = ImportProposalMatching.keepsRef(
      accepted.map((item) => refOf(item)),
      isTarget
    );
    return {
      updates: accepted.filter((_item, index) => keepsRef[index]),
      additions: accepted.filter((_item, index) => !keepsRef[index]),
    };
  }

  private static _mergeSectionQaPairs(
    existing: IAIReceptionistSectionQaPairs | undefined,
    proposed: SectionQaPairLists<IImportProposedQaPair>,
    acceptedIndexes: SectionQaPairLists<number>,
    newId: () => string
  ): IAIReceptionistSectionQaPairs | undefined {
    const acceptedByKey = IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS.map((key) => ({
      key,
      accepted: ImportProposalAcceptance._pickAccepted(
        proposed[key],
        acceptedIndexes[key]
      ),
    })).filter(({ accepted }) => accepted.length > 0);
    if (acceptedByKey.length === 0) {
      return existing;
    }
    const merged: IAIReceptionistSectionQaPairs = { ...existing };
    acceptedByKey.forEach(({ key, accepted }) => {
      merged[key] = ImportProposalAcceptance._mergeQaPairs(
        existing?.[key],
        accepted,
        newId
      );
    });
    return merged;
  }

  /**
   * An update keeps the existing pair's id, question and position and
   * replaces only its answer, so the practice's own wording of the question
   * stays.
   */
  private static _mergeQaPairs(
    existing: IAIReceptionistCustomQA[] | undefined,
    accepted: IImportProposedQaPair[],
    newId: () => string
  ): IAIReceptionistCustomQA[] {
    const current = existing ?? [];
    const { updates, additions } = ImportProposalAcceptance._splitUpdates(
      accepted,
      (pair) => pair.existingId,
      (existingId) => current.some((pair) => pair.id === existingId)
    );
    const updated = current.map((pair) => {
      const update = updates.find(
        (candidate) => candidate.existingId === pair.id
      );
      return update ? { ...pair, answer: update.answer } : pair;
    });
    return ImportProposalAcceptance._appendWithinCap(
      updated,
      additions,
      (pair) => ImportProposalAcceptance._toKnowledgeBaseQaPair(pair, newId)
    );
  }

  /**
   * An update replaces only the content of the section at the referenced
   * position and keeps its title and Q&A pairs. The position alone decides,
   * because the extractor may reword the title and the review showed the
   * practice the section at that position beside the proposal.
   */
  private static _mergeCustomSections(
    existing: IAIReceptionistCustomSection[] | undefined,
    proposed: IImportProposedCustomSection[],
    acceptedIndexes: number[],
    newId: () => string
  ): IAIReceptionistCustomSection[] | undefined {
    const accepted = ImportProposalAcceptance._pickAccepted(
      proposed,
      acceptedIndexes
    );
    if (accepted.length === 0) {
      return existing;
    }
    const current = existing ?? [];
    const { updates, additions } = ImportProposalAcceptance._splitUpdates(
      accepted,
      (section) => section.existingIndex,
      (existingIndex) =>
        ImportProposalMatching.isIndexInRange(existingIndex, current.length)
    );
    const updated = current.map((section, position) => {
      const update = updates.find(
        (candidate) => candidate.existingIndex === position
      );
      return update ? { ...section, content: update.content } : section;
    });
    return ImportProposalAcceptance._appendWithinCap(
      updated,
      additions,
      (section) =>
        ImportProposalAcceptance._toKnowledgeBaseCustomSection(section, newId)
    );
  }

  /**
   * Returns the proposed items at the accepted indexes, in proposal order, so
   * an index that is out of range or repeated selects nothing extra.
   */
  private static _pickAccepted<Item>(
    items: Item[] | undefined,
    acceptedIndexes: number[] | undefined
  ): Item[] {
    const acceptedIndexSet = new Set(acceptedIndexes);
    return (items ?? []).filter((_item, index) => acceptedIndexSet.has(index));
  }

  /**
   * Keeps every existing item and appends only as many new items as the
   * provider's collection cap leaves room for. New items are built after the
   * cap is applied, so no id is generated for an item that is not kept.
   */
  private static _appendWithinCap<Existing, Addition>(
    existing: Existing[] | undefined,
    additions: Addition[],
    toItem: (addition: Addition) => Existing
  ): Existing[] {
    const current = existing ?? [];
    const room = Math.max(
      0,
      KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS - current.length
    );
    return [
      ...current,
      ...additions.slice(0, room).map((addition) => toItem(addition)),
    ];
  }

  private static _toKnowledgeBaseQaPair(
    pair: IImportProposedQaPair,
    newId: () => string
  ): IAIReceptionistCustomQA {
    return { id: newId(), question: pair.question, answer: pair.answer };
  }

  private static _toKnowledgeBaseCustomSection(
    section: IImportProposedCustomSection,
    newId: () => string
  ): IAIReceptionistCustomSection {
    return {
      title: section.title,
      content: section.content,
      ...(section.qa
        ? {
            qa: section.qa.map((pair) =>
              ImportProposalAcceptance._toKnowledgeBaseQaPair(pair, newId)
            ),
          }
        : {}),
    };
  }
}
